// 历史版本服务
// 用于管理和恢复 Supabase 中的历史版本数据

import { supabase, isSupabaseAvailable } from '@ext-bookmark/shared/api';
import type { AppData } from '@ext-bookmark/shared/types';
import { getCurrentUser } from '../auth/userService';

export interface HistoryVersion {
  id: string;
  version_number: number;
  created_at: string;
  app_data: AppData;
}

/**
 * 获取用户的所有历史版本
 */
export async function getUserDataHistory(): Promise<{ versions: HistoryVersion[]; error: string | null }> {
  if (!isSupabaseAvailable() || !supabase) {
    return {
      versions: [],
      error: 'Supabase 未配置',
    };
  }

  const user = await getCurrentUser();
  if (!user) {
    return {
      versions: [],
      error: '用户未登录',
    };
  }

  try {
    const { data, error } = await supabase
      .from('user_data_history')
      .select('id, version_number, created_at, app_data')
      .eq('user_id', user.id)
      .order('version_number', { ascending: false });

    if (error) {
      // 如果表不存在，返回空数组而不是错误
      if (error.code === 'PGRST116' || error.message.includes('does not exist')) {
        return {
          versions: [],
          error: null,
        };
      }

      return {
        versions: [],
        error: error.message || '获取历史版本失败',
      };
    }

    return {
      versions: (data || []).map((item) => ({
        id: item.id,
        version_number: item.version_number,
        created_at: item.created_at,
        app_data: item.app_data as AppData,
      })),
      error: null,
    };
  } catch (error: any) {
    return {
      versions: [],
      error: error.message || '获取历史版本时发生未知错误',
    };
  }
}

/**
 * 恢复指定的历史版本
 */
export async function restoreHistoryVersion(versionNumber: number): Promise<{ error: string | null }> {
  if (!isSupabaseAvailable() || !supabase) {
    return {
      error: 'Supabase 未配置',
    };
  }

  const user = await getCurrentUser();
  if (!user) {
    return {
      error: '用户未登录',
    };
  }

  try {
    // 获取指定的历史版本
    const { data, error: fetchError } = await supabase
      .from('user_data_history')
      .select('app_data')
      .eq('user_id', user.id)
      .eq('version_number', versionNumber)
      .single();

    if (fetchError || !data) {
      return {
        error: '找不到指定的历史版本',
      };
    }

    // 恢复数据到 user_data 表
    const now = new Date().toISOString();
    const { error: restoreError } = await supabase
      .from('user_data')
      .upsert(
        {
          user_id: user.id,
          app_data: data.app_data,
          updated_at: now,
        },
        {
          onConflict: 'user_id',
        }
      );

    if (restoreError) {
      return {
        error: restoreError.message || '恢复历史版本失败',
      };
    }

    return { error: null };
  } catch (error: any) {
    return {
      error: error.message || '恢复历史版本时发生未知错误',
    };
  }
}

/**
 * 删除指定的历史版本
 */
export async function deleteHistoryVersion(versionNumber: number): Promise<{ error: string | null }> {
  if (!isSupabaseAvailable() || !supabase) {
    return {
      error: 'Supabase 未配置',
    };
  }

  const user = await getCurrentUser();
  if (!user) {
    return {
      error: '用户未登录',
    };
  }

  try {
    const { error } = await supabase
      .from('user_data_history')
      .delete()
      .eq('user_id', user.id)
      .eq('version_number', versionNumber);

    if (error) {
      return {
        error: error.message || '删除历史版本失败',
      };
    }

    return { error: null };
  } catch (error: any) {
    return {
      error: error.message || '删除历史版本时发生未知错误',
    };
  }
}
